import React, { useEffect, useRef } from 'react';
import { RowHeightParams } from '@ag-grid-community/core';
import { AGValidatedCellParams } from './AGGridTable.types';

/**
 * Drop-in cell renderer for AG-Grid that shows an input with optional
 * error message below and an optional character counter.
 */
function AGValidatedCell(params: AGValidatedCellParams) {
  const {
    value,
    data,
    colDef,
    api,
    node,
    onValueChange,
    getError,
    placeholder = '',
    maxLength,
    disabled = false,
    inputType = 'text',
    showErrorBorder = true,
    showErrorMessage = true,
  } = params;

  const field = colDef?.field;
  const error = getError ? getError(data, field) : null;
  const prevErrorRef = useRef(error);

  useEffect(() => {
    if (prevErrorRef.current !== error) {
      prevErrorRef.current = error;
      if (api && node) {
        node.setRowHeight(null);
        api.onRowHeightChanged();
      }
    }
  }, [error, api, node]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onValueChange) onValueChange(data, field, e.target.value);
  };

  const hasError = Boolean(error);
  const applyBorder = hasError && showErrorBorder;
  const showMessage = hasError && showErrorMessage;

  return (
    <div className="ag-validated-cell">
      <div className="ag-validated-cell__input-row">
        <input
          type={inputType}
          className={`ag-validated-cell__input${applyBorder ? ' ag-validated-cell__input--error' : ''}`}
          value={value ?? ''}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength ?? undefined}
          disabled={disabled}
        />
        {maxLength != null && (
          <span className="ag-validated-cell__count">
            {(value ?? '').length}/{maxLength}
          </span>
        )}
      </div>
      {showMessage && <span className="ag-validated-cell__error">{error}</span>}
    </div>
  );
}

/**
 * Returns a getRowHeight function for AG-Grid gridOptions.
 * Rows with an error get errorHeight, others get normalHeight.
 */
AGValidatedCell.getRowHeight = function (
  getErrorFn: (data: unknown, field: string) => string | null,
  fields?: string[],
  errorHeight = 72,
  normalHeight = 40,
) {
  return (params: RowHeightParams): number => {
    if (!getErrorFn || !params.data) return normalHeight;
    const keys = fields ?? Object.keys(params.data as Record<string, unknown>);
    const hasError = keys.some((f) => Boolean(getErrorFn(params.data, f)));
    return hasError ? errorHeight : normalHeight;
  };
};

export default AGValidatedCell;
